<template>
  <!-- Invisible probe: identifies the parent element that acts as the
       activator (mirrors v-tooltip's activator="parent") -->
  <span ref="probeRef" class="tech_tooltip_probe" aria-hidden="true"></span>

  <Teleport to="body">
    <Transition name="tech-tooltip">
      <span
        v-if="visible"
        :id="id"
        role="tooltip"
        class="tech_tooltip"
        v-bind="$attrs"
        :style="bubbleStyle">
        <slot>{{ text }}</slot>
      </span>
    </Transition>
  </Teleport>
</template>

<script lang="ts">
// Module scope: shared across all Tooltip instances for unique ids
let uidCounter = 0;
</script>

<script setup lang="ts">
/**
 * Tooltip — Vuetify-free replacement for v-tooltip (plan Phase 4a).
 *
 * Deliberately minimal: every v-tooltip in the codebase was
 * activator="parent" + location="bottom" with text content, so that is
 * the entire API. The bubble teleports to <body> (never clipped by
 * overflow:hidden ancestors), shows on parent hover/focus, hides on
 * Escape, and is wired to the trigger via aria-describedby.
 * Class/style fall through to the bubble (e.g. hidden-xs).
 */
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';

defineOptions({ inheritAttrs: false });

const props = withDefaults(
  defineProps<{
    text?: string;
    /** ms before showing (v-tooltip open-delay) */
    openDelay?: number | string;
    maxWidth?: number | string;
  }>(),
  { openDelay: 0 }
);

const id = `tech-tooltip-${++uidCounter}`;

const probeRef = ref<HTMLElement | null>(null);
const visible = ref(false);
const pos = ref({ left: 0, top: 0 });
let parent: HTMLElement | null = null;
let openTimer: number | undefined;

// Same gap v-tooltip left between activator and bubble
const OFFSET = 10;

const bubbleStyle = computed(() => ({
  left: `${pos.value.left}px`,
  top: `${pos.value.top}px`,
  maxWidth: props.maxWidth ? `${Number(props.maxWidth) || props.maxWidth}px` : undefined,
}));

const updatePosition = () => {
  if (!parent) return;
  const r = parent.getBoundingClientRect();
  pos.value = { left: r.left + r.width / 2, top: r.bottom + OFFSET };
};

const onEscape = (e: KeyboardEvent) => {
  if (e.key === 'Escape') hide();
};

const show = () => {
  window.clearTimeout(openTimer);
  openTimer = window.setTimeout(() => {
    updatePosition();
    visible.value = true;
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    document.addEventListener('keydown', onEscape);
  }, Number(props.openDelay) || 0);
};

const hide = () => {
  window.clearTimeout(openTimer);
  visible.value = false;
  window.removeEventListener('scroll', updatePosition, true);
  window.removeEventListener('resize', updatePosition);
  document.removeEventListener('keydown', onEscape);
};

let setDescribedBy = false;

onMounted(() => {
  parent = probeRef.value?.parentElement ?? null;
  if (!parent) return;
  parent.addEventListener('mouseenter', show);
  parent.addEventListener('mouseleave', hide);
  parent.addEventListener('focusin', show);
  parent.addEventListener('focusout', hide);
  if (!parent.hasAttribute('aria-describedby')) {
    parent.setAttribute('aria-describedby', id);
    setDescribedBy = true;
  }
});

onBeforeUnmount(() => {
  hide();
  if (!parent) return;
  parent.removeEventListener('mouseenter', show);
  parent.removeEventListener('mouseleave', hide);
  parent.removeEventListener('focusin', show);
  parent.removeEventListener('focusout', hide);
  if (setDescribedBy) parent.removeAttribute('aria-describedby');
});
</script>

<style lang="scss">
.tech_tooltip_probe {
  display: none;
}

// Bubble styling ported from Vuetify's compiled v-tooltip overlay
.tech_tooltip {
  position: fixed;
  transform: translateX(-50%);
  z-index: 2400;
  background: rgb(var(--v-theme-surface-variant));
  color: rgb(var(--v-theme-on-surface-variant));
  border-radius: 4px;
  font-size: 0.875rem;
  line-height: 1.6;
  display: inline-block;
  padding: 5px 16px;
  text-transform: initial;
  width: auto;
  max-width: calc(100vw - 16px);
  pointer-events: none;
  overflow-wrap: break-word;
}

// Tooltips are hover affordances: hide them entirely on touch devices,
// where there is no hover and tap-focus would make the bubble stick.
// (Replaces the per-call-site hidden-xs classes.)
@media (hover: none), (pointer: coarse) {
  .tech_tooltip {
    display: none !important;
  }
}

// v-tooltip's enter/leave timings
.tech-tooltip-enter-active {
  transition: opacity 0.15s cubic-bezier(0, 0, 0.2, 1);
}
.tech-tooltip-leave-active {
  transition: opacity 75ms cubic-bezier(0.4, 0, 1, 1);
}
.tech-tooltip-enter-from,
.tech-tooltip-leave-to {
  opacity: 0;
}
</style>
